import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "vortex_customer_token";

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const setToken = (t) => {
        if (t) {
            localStorage.setItem(TOKEN_KEY, t);
            api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
        } else {
            localStorage.removeItem(TOKEN_KEY);
            delete api.defaults.headers.common["Authorization"];
        }
    };

    const checkAuth = useCallback(async () => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setLoading(false);
            return;
        }
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setToken(null);
            setUser(null);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const register = async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name });
        setToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const exchangeGoogleSession = async (session_id) => {
        const { data } = await api.post("/auth/google/session", { session_id });
        setToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const setSessionFromReset = async (token, userData) => {
        setToken(token);
        if (userData) setUser(userData);
        else await checkAuth();
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch { /* ignore */ }
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, exchangeGoogleSession, setSessionFromReset, refresh: checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
